/*
 * CEOChallengeResults — Final results screen.
 * Shows achievement level, category rollup, review of missed questions,
 * cert download (if passed), retake, and back-to-dashboard.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Award, Download, RefreshCcw, Home, CheckCircle2, XCircle } from "lucide-react";
import { LemonBot, Confetti, THEME, Chip } from "./ceo_ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CEOChallengeResults() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/ceo-challenge/attempts/${attemptId}`);
        if (!alive) return;
        setData(res.data);
      } catch (e) {
        console.error(e);
      }
    })();
    // Auto-hide confetti
    const t = setTimeout(() => setShowConfetti(false), 3000);
    return () => { alive = false; clearTimeout(t); };
  }, [attemptId]);

  if (!data) {
    return (
      <div className={`${THEME.bg} text-white min-h-screen flex items-center justify-center`}>
        <p>Loading your results…</p>
      </div>
    );
  }

  const { attempt, achievement, categories, review, badges } = data;
  const passed = badges.includes("ceo_final_challenge_graduate");
  const durationMin = Math.floor((attempt.completion_seconds || 0) / 60);
  const durationSec = (attempt.completion_seconds || 0) % 60;

  const downloadCert = () => {
    const token = localStorage.getItem("token");
    const url = `${BACKEND}/api/ceo-challenge/certificate/${attempt.id}.pdf`;
    // Fetch with auth then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ceo-challenge-certificate.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  };

  return (
    <div className={`${THEME.bg} text-white min-h-screen relative overflow-hidden`} data-testid="ceo-results-root">
      <Confetti active={showConfetti} count={90} />

      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-25" aria-hidden="true">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#7C3AED] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#FFD500] blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-10 sm:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <LemonBot size={110} mood={passed ? "cheer" : "happy"} data-testid="ceo-results-lemonbot" />
          <p className="mt-6 text-xs font-black tracking-[0.28em] uppercase text-[#FFD500]">
            Youth CEO Camp — Final Challenge
          </p>
          <h1
            className="mt-2 text-[38px] sm:text-5xl lg:text-[64px] font-black leading-[0.98]"
            data-testid="ceo-results-title"
          >
            {achievement.emoji} {achievement.label}
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-white/80 text-base sm:text-lg leading-relaxed">
            {achievement.message}
          </p>
        </div>

        {/* Score grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <ScoreTile label="Correct" value={`${attempt.correct_count}/20`} accent="#FFD500" testId="ceo-results-correct" />
          <ScoreTile label="Score" value={attempt.score} accent="#22C55E" testId="ceo-results-score" />
          <ScoreTile label="Percentage" value={`${attempt.percentage}%`} accent="#7C3AED" testId="ceo-results-pct" />
          <ScoreTile
            label="Time"
            value={`${durationMin}:${String(durationSec).padStart(2, "0")}`}
            accent="#38BDF8"
            testId="ceo-results-time"
          />
        </div>

        {/* Category breakdown */}
        <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 sm:p-8 mb-8" data-testid="ceo-results-categories">
          <h2 className="text-2xl font-black mb-4">Category breakdown</h2>
          <div className="space-y-4">
            {categories.map((c) => (
              <div key={c.key}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <p className="font-bold text-sm sm:text-base">{c.label}</p>
                  <p className="text-sm text-white/70 font-bold">
                    {c.correct}/{c.total} <span className="text-white/50">· {c.percentage}%</span>
                  </p>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${c.percentage}%`,
                      background:
                        c.percentage >= 80 ? "#22C55E" : c.percentage >= 50 ? "#FFD500" : "#F87171",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
          <button
            data-testid="ceo-results-review-toggle"
            onClick={() => setShowReview((s) => !s)}
            className="inline-flex items-center justify-center gap-2 bg-white/10 ring-1 ring-white/20 text-white font-bold text-base rounded-2xl py-3 hover:bg-white/15 transition-colors"
          >
            {showReview ? "Hide Review" : "Review Answers"}
          </button>
          <button
            data-testid="ceo-results-retake"
            onClick={() => nav("/ceo-challenge")}
            className="inline-flex items-center justify-center gap-2 bg-white/10 ring-1 ring-white/20 text-white font-bold text-base rounded-2xl py-3 hover:bg-white/15 transition-colors"
          >
            <RefreshCcw className="w-4 h-4" /> Try Again
          </button>
          <button
            data-testid="ceo-results-dashboard"
            onClick={() => nav("/dashboard")}
            className="inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black font-black text-base rounded-2xl py-3 hover:bg-white transition-colors"
          >
            <Home className="w-4 h-4" /> Dashboard
          </button>
        </div>

        {/* Badges + certificate */}
        {passed && (
          <div className="rounded-3xl bg-gradient-to-br from-[#FFD500]/15 to-[#7C3AED]/15 ring-1 ring-[#FFD500]/40 p-6 sm:p-8 mb-8" data-testid="ceo-results-badge-panel">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-[#FFD500] flex items-center justify-center text-4xl shrink-0" aria-hidden="true">
                <Award className="w-10 h-10 text-black" />
              </div>
              <div className="flex-1">
                <Chip tone="yellow">Badge unlocked</Chip>
                <h3 className="mt-2 text-xl sm:text-2xl font-black">
                  Youth CEO Camp Final Challenge Graduate
                </h3>
                {badges.includes("ceo_champion") && (
                  <p className="mt-1 text-[#FFD500] font-bold text-sm">
                    + CEO Champion Badge (18+ correct)
                  </p>
                )}
                <p className="mt-2 text-sm text-white/80">
                  Download a personalized certificate signed by Brandon Ward, ASBC®.
                </p>
              </div>
              <button
                data-testid="ceo-results-download-cert"
                onClick={downloadCert}
                className="inline-flex items-center gap-2 bg-white text-black font-black rounded-full px-5 py-3 hover:bg-[#FFD500] transition-colors"
              >
                <Download className="w-4 h-4" /> Certificate
              </button>
            </div>
          </div>
        )}

        {/* Answer review */}
        {showReview && (
          <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-5 sm:p-6" data-testid="ceo-results-review">
            <h2 className="text-2xl font-black mb-4">Review your answers</h2>
            <div className="space-y-3">
              {review.map((r) => (
                <div key={r.question_number} className={`rounded-2xl p-4 ring-1 ${r.correct ? "ring-[#22C55E]/40 bg-[#22C55E]/5" : "ring-[#F87171]/40 bg-[#F87171]/5"}`}>
                  <div className="flex items-start gap-3">
                    {r.correct ? <CheckCircle2 className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[#F87171] shrink-0 mt-0.5" />}
                    <div className="flex-1">
                      <p className="text-xs font-black uppercase tracking-widest text-white/50 mb-0.5">Q{r.question_number}</p>
                      <p className="font-bold text-sm sm:text-base leading-snug">{r.question_text}</p>
                      <p className="mt-2 text-xs sm:text-sm text-white/70">
                        Your answer: <span className="font-bold text-white">{r.selected_answer}</span>
                        {" · "}Correct: <span className="font-bold text-[#22C55E]">{r.correct_answer}</span>
                      </p>
                      {!r.correct && (
                        <p className="mt-2 text-sm text-white/70 leading-relaxed">
                          <span className="font-bold text-white">Why: </span>{r.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-center text-sm text-white/60 max-w-xl mx-auto">
          Great CEOs keep learning, improving, and taking action. Review your answers,
          practice your pitch, and get ready to lead.
        </p>
      </div>
    </div>
  );
}

function ScoreTile({ label, value, accent, testId }) {
  return (
    <div
      className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 text-center"
      data-testid={testId}
      style={{ borderTop: `4px solid ${accent}` }}
    >
      <p className="text-[10px] font-black uppercase tracking-widest text-white/60">{label}</p>
      <p className="mt-2 text-2xl sm:text-3xl font-black text-white">{value}</p>
    </div>
  );
}
